import type { ReadingCandidate } from "../corpus/selection";

export type ShelfEdition = "retelling" | "original";

/** One whole publication on the shelf, read as its chapters in order. */
export interface ShelfSeries {
  publicationId: string;
  title: string;
  author: string | null;
  coverImage: string | null;
  chapterReadingIds: readonly string[];
  edition: ShelfEdition;
  /** The retelling of an original, or the original of a retelling. */
  counterpartPublicationId: string | null;
}

export interface ReadTheOriginalBanner {
  kind: "read_the_original";
}

export interface ShelfEntry {
  series: ShelfSeries;
  banner?: ReadTheOriginalBanner;
}

export type SeriesProgress = "not_started" | "in_progress" | "completed";

/** The part of a series' projected fit the shelf gates on. */
export interface SeriesFitVerdict {
  eligible: boolean;
}

export interface ReadingProgress {
  completedReadingIds: ReadonlySet<string>;
  inProgressReadingIds: ReadonlySet<string>;
}

export function seriesProgress(
  series: Pick<ShelfSeries, "chapterReadingIds">,
  progress: ReadingProgress,
): SeriesProgress {
  const chapters = series.chapterReadingIds;
  if (isEveryChapterCompleted(chapters, progress.completedReadingIds)) {
    return "completed";
  }
  const touched = chapters.some(
    (id) =>
      progress.completedReadingIds.has(id) ||
      progress.inProgressReadingIds.has(id),
  );
  return touched ? "in_progress" : "not_started";
}

function isEveryChapterCompleted(
  chapterReadingIds: readonly string[],
  completedReadingIds: ReadonlySet<string>,
): boolean {
  return (
    chapterReadingIds.length > 0 &&
    chapterReadingIds.every((id) => completedReadingIds.has(id))
  );
}

/**
 * A series in progress is never gated; one not yet started shows only when its projected fit
 * passes; a completed series has no next chapter to offer.
 */
export function isSeriesOnShelf(
  progress: SeriesProgress,
  fit: SeriesFitVerdict,
): boolean {
  switch (progress) {
    case "in_progress":
      return true;
    case "not_started":
      return fit.eligible;
    case "completed":
      return false;
  }
}

export function readTheOriginalBanner(
  series: ShelfSeries,
  counterpart: ShelfSeries | undefined,
  completedReadingIds: ReadonlySet<string>,
): ReadTheOriginalBanner | undefined {
  if (series.edition !== "original" || counterpart === undefined) {
    return undefined;
  }
  const isItsRetelling =
    counterpart.edition === "retelling" &&
    series.counterpartPublicationId === counterpart.publicationId;
  if (!isItsRetelling) return undefined;
  return isEveryChapterCompleted(
    counterpart.chapterReadingIds,
    completedReadingIds,
  )
    ? { kind: "read_the_original" }
    : undefined;
}

export function shelfEntry(
  series: ShelfSeries,
  counterpart: ShelfSeries | undefined,
  completedReadingIds: ReadonlySet<string>,
): ShelfEntry {
  const banner = readTheOriginalBanner(
    series,
    counterpart,
    completedReadingIds,
  );
  return banner === undefined ? { series } : { series, banner };
}

/**
 * A ranked text the reader may pick. A passage reaches here only once an editor has promoted
 * it to a text of its own; `derivedFromTextId` names the text it was cut from.
 */
export type Recommendation = ReadingCandidate & {
  derivedFromTextId?: string;
};

export type PresentedChoice =
  | { kind: "recommendation"; recommendation: Recommendation }
  | { kind: "starter_worksheet" };

export const MAX_PRESENTED_CHOICES = 4;

/** Below this many recommendations the starter Sentence Worksheet is offered as well. */
export const STARTER_WORKSHEET_BELOW_RECOMMENDATIONS = 3;

export function recommendationSourceTextId(
  recommendation: Recommendation,
): string {
  return recommendation.derivedFromTextId ?? recommendation.id;
}

/** Higher ranks first. The one place a richer fit score replaces challengeScore. */
export function recommendationRank(recommendation: Recommendation): number {
  return recommendation.challengeScore;
}

/**
 * The choices a reader is shown: best-ranked first, one per source text, at most
 * MAX_PRESENTED_CHOICES, with the starter worksheet appended whenever fewer than
 * STARTER_WORKSHEET_BELOW_RECOMMENDATIONS recommendations survive. Derived from the pool alone,
 * so the worksheet returns as soon as the pool shrinks again.
 */
export function presentRecommendations(
  pool: readonly Recommendation[],
): PresentedChoice[] {
  const ranked = [...pool].sort(
    (a, b) => recommendationRank(b) - recommendationRank(a),
  );
  const seenSourceTextIds = new Set<string>();
  const choices: PresentedChoice[] = [];
  for (const recommendation of ranked) {
    if (choices.length === MAX_PRESENTED_CHOICES) break;
    const sourceTextId = recommendationSourceTextId(recommendation);
    if (seenSourceTextIds.has(sourceTextId)) continue;
    seenSourceTextIds.add(sourceTextId);
    choices.push({ kind: "recommendation", recommendation });
  }
  if (choices.length < STARTER_WORKSHEET_BELOW_RECOMMENDATIONS) {
    choices.push({ kind: "starter_worksheet" });
  }
  return choices;
}

/**
 * After a text cut from a series, link to that series only where the shelf would show it, so a
 * finished passage never points at a series the reader is not offered.
 */
export function shouldForegroundSourceSeries(
  recommendation: Recommendation,
  sourceSeriesProgress: SeriesProgress,
  sourceSeriesFit: SeriesFitVerdict,
): boolean {
  return (
    recommendation.derivedFromTextId !== undefined &&
    isSeriesOnShelf(sourceSeriesProgress, sourceSeriesFit)
  );
}
